import { computed } from 'vue';

const cursorSvgUrls = import.meta.glob('../../../images/cursors/**/*.svg', { eager: true, query: '?url', import: 'default' });

/**
 * Resolves SVG asset(s) for a given cursor type + state.
 * Tries a single-layer file first (e.g. `default.svg`),
 * then falls back to dual-layer variants (`default_1.svg` / `default_2.svg`).
 *
 * @param {string} type  - Cursor type key (e.g. 'GLASS', 'FIGMA')
 * @param {string} state - Cursor state (e.g. 'default', 'hover', 'project_hover')
 * @returns {{ layer1: string|null, layer2: string|null } | null}
 */
export function getCursorSvg(type, state) {
  const url = (name) => cursorSvgUrls[`../../../images/cursors/${type}/${name}.svg`] ?? null;

  const plain = url(state);
  if (plain) return { layer1: plain, layer2: null };

  const layer1 = url(`${state}_1`);
  const layer2 = url(`${state}_2`);
  if (layer1 || layer2) return { layer1, layer2 };

  return null;
}

/**
 * Composable that returns computed SVG assets for all three cursor states
 * reactive to the current cursor type.
 *
 * @param {import('vue').ComputedRef<string>} cursorType
 */
export function useCursorSvg(cursorType) {
  const defaultCursorSvg = computed(() =>
    cursorType.value !== 'DEFAULT' ? getCursorSvg(cursorType.value, 'default') : null
  );

  const hoverCursorSvg = computed(() =>
    cursorType.value !== 'DEFAULT' ? getCursorSvg(cursorType.value, 'hover') : null
  );

  const projectHoverCursorSvg = computed(() =>
    cursorType.value !== 'DEFAULT' ? getCursorSvg(cursorType.value, 'project_hover') : null
  );

  /**
   * Flat list of { name, svg } objects — convenient for v-for loops in templates.
   * Only includes states that actually have SVG assets.
   */
  const cursorStates = computed(() => [
    { name: 'default', svg: defaultCursorSvg.value },
    { name: 'hover',   svg: hoverCursorSvg.value },
    { name: 'project', svg: projectHoverCursorSvg.value },
  ].filter(s => s.svg));

  return { defaultCursorSvg, hoverCursorSvg, projectHoverCursorSvg, cursorStates };
}
